import { CreateRecord, op, Operation } from "libs/transaction";
import { ActionContext, ActionCreator, createAction } from "./action";
import { InvalidActionStateError } from "./utils/errors";
import {
  generateRecordId,
  getPointer,
  InboxSectionTagRecord,
  InboxSubsectionTagRecord,
  RecordValue,
} from "libs/schema";
import * as ops from "libs/operations";
import { compact, groupBy, pick } from "lodash-comms";
import { findMatchingSubsection } from "libs/searchQueryMatcher";
import { createMemoizedDatabase } from "libs/database";
import { isDefined } from "../../predicates";
import dayjs from "dayjs";
import { authorizeUserCanUpdateNotifications } from "./authorizers/notificationAuthorizers";
import { authorizeUserHasAccessToTags } from "./authorizers/tagAuthorizers";

/* -------------------------------------------------------------------------------------------------
 *  triageThreads
 * -------------------------------------------------------------------------------------------------
 */

type TriageThreadsProps = {
  timestamp: Date | string;
  threads: TriageThreadData[];
};

export type TriageThreadData = {
  threadId: string;
  /**
   * We expect the notification to have a specific message_id value. If the notification doesn't,
   * then that indicates that a new message has been delivered to this thread since the user
   * constructed this action. If this happens, our triage action is liable to overwrite any updates
   * to this notification caused by the delivery of the new message, which would be undesireable.
   * In this case we'll choose to throw an error instead of run this action.
   */
  expectedNotificationMessageId: string | null | undefined;

  /**
   * If the user has lost permission to access this thread, provide `null`. Else
   * provide the appropriate values.
   */
  lastMessage: {
    id: string;
    timelineOrder: string;
    sentAt: string;
  } | null;
  /**
   * This should be passed in for any `done: false` triages so we can restore the seen and read receipts if we're
   * marking a thread as undone (i.e. was previously done, now it's not).
   *
   * If set to undefined, no changes will be made to the receipts.
   */
  receiptValuesToRestore?: {
    upToTimelineId: string;
    upToTimelineOrder: string;
  } | null;
  /**
   * If this is passed in, we will use it on the server to preemptively "index" the notification under those tags
   * until the next reindex run.
   */
  newMatchingTagIds?: string[];
  done?: boolean;
  isStarred?: boolean;
  remindAt?: string | null;
  resolveRequests?: true;
};

export const triageThreads: ActionCreator<TriageThreadsProps> = {
  name: "notification.triageThreads",
  action: async (props, context) => {
    const { loader } = context.environment;

    const { threads } = props;

    if (!threads.length) {
      throw new InvalidActionStateError("[triageThreads] Need at least one thread to triage", { errorData: { props } });
    }

    const [userProfile] = await loader.getRecord("user_profile", context.currentUserId);

    if (!userProfile) {
      throw new InvalidActionStateError("[triageThreads] User profile not found", { errorData: { props } });
    }

    const currentTimestamp = new Date(props.timestamp).toISOString();
    const newMatchingTagIdsByThreadId: { [threadId: string]: string[] | undefined } = {};

    const results = await Promise.allSettled(
      threads.map(async (threadData): Promise<Operation[]> => {
        if (
          threadData.done === undefined &&
          threadData.remindAt === undefined &&
          threadData.isStarred === undefined &&
          threadData.resolveRequests === undefined
        ) {
          throw new InvalidActionStateError("[triageThreads] empty update", {
            errorData: { threadData },
          });
        }

        const [[notification], [requests]] = await Promise.all([
          loader.getRecord(
            getPointer("notification", {
              thread_id: threadData.threadId,
              user_id: context.currentUserId,
            }),
          ),
          loader.getThreadRequests({ thread_id: threadData.threadId, recipient_user_id: context.currentUserId }),
        ]);

        const operations: Operation[] = [];

        if (notification) {
          if (isDefined(threadData.expectedNotificationMessageId)) {
            if (notification.message_id !== threadData.expectedNotificationMessageId) {
              throw new InvalidActionStateError("[triageThreads] expected notification message ID has changed", {
                errorData: {
                  notification,
                  threadData,
                },
              });
            }
          }

          operations.push(
            ...ops.notification.triageNotification({
              notification,
              lastMessage: threadData.lastMessage,
              isDone: threadData.done,
              remindAt: threadData.remindAt,
              isStarred: threadData.isStarred,
              areRequestsResolved: threadData.resolveRequests,
              currentTimestamp,
            }),
          );

          // If we're marking a notification as not done, and it was previously marked as done, we need to delete the
          // receipts or set them back to their previous values
          if (threadData.done === false && notification.is_done && threadData.receiptValuesToRestore !== undefined) {
            if (!threadData.receiptValuesToRestore) {
              // We're doubling up on the deletes per table since we create the initial read receipts with the wrong
              // value for thread_id in `getCreateNotificationOperations`. But we use the right value for thread_id
              // in some cases so we'll just handle both cases until we fix the id/thread_id issue.
              operations.push(
                op.delete(
                  getPointer("thread_read_receipt", {
                    thread_id: threadData.threadId,
                    user_id: context.currentUserId,
                  }),
                ),
                op.delete(
                  getPointer("thread_seen_receipt", {
                    thread_id: threadData.threadId,
                    user_id: context.currentUserId,
                  }),
                ),
              );
            } else {
              operations.push(
                op.set("thread_read_receipt", {
                  id: generateRecordId("thread_read_receipt", {
                    thread_id: notification.thread_id,
                    user_id: notification.user_id,
                  }),
                  owner_organization_id: notification.owner_organization_id,
                  thread_id: notification.thread_id,
                  user_id: notification.user_id,
                  read_to_timeline_id: threadData.receiptValuesToRestore.upToTimelineId,
                  read_to_timeline_order: threadData.receiptValuesToRestore.upToTimelineOrder,
                }),
                op.set("thread_seen_receipt", {
                  id: generateRecordId("thread_seen_receipt", {
                    thread_id: notification.thread_id,
                    user_id: notification.user_id,
                  }),
                  owner_organization_id: notification.owner_organization_id,
                  thread_id: notification.thread_id,
                  user_id: notification.user_id,
                  seen_to_timeline_id: threadData.receiptValuesToRestore.upToTimelineId,
                  seen_to_timeline_order: threadData.receiptValuesToRestore.upToTimelineOrder,
                }),
              );
            }
          }
        } else {
          if (isDefined(threadData.expectedNotificationMessageId)) {
            if (threadData.expectedNotificationMessageId !== null) {
              throw new InvalidActionStateError("[triageThreads] expected notification message ID has changed", {
                errorData: {
                  notification: null,
                  threadData,
                },
              });
            }
          }

          if (threadData.lastMessage) {
            const results = await getCreateNotificationOperationsAndMatchingTagIds(context, {
              ownerOrganizationId: userProfile.owner_organization_id,
              threadId: threadData.threadId,
              done: threadData.done,
              lastMessage: threadData.lastMessage,
              isStarred: threadData.isStarred,
              remindAt: threadData.remindAt,
              areRequestsResolved: threadData.resolveRequests,
              newMatchingTagIds: threadData.newMatchingTagIds,
              currentUsersRequests: requests,
            });

            newMatchingTagIdsByThreadId[threadData.threadId] = results.matchingTagIds;

            operations.push(...results.operations);
          } else {
            throw new InvalidActionStateError("[triageThreads] no existing notification and no last message data", {
              errorData: { threadData },
            });
          }
        }

        if (threadData.resolveRequests) {
          const { lastMessage } = threadData;

          if (!lastMessage) {
            throw new InvalidActionStateError(
              `[triageThread] No last message found for thread ${threadData.threadId}`,
              { errorData: { threadData } },
            );
          }

          operations.push(
            ...requests.flatMap((request) =>
              ops.threadRequest.updateThreadRequestLastResolvedAt({
                request,
                lastResolvedAt: {
                  sent_at: lastMessage.sentAt,
                  timeline_order: lastMessage.timelineOrder,
                },
              }),
            ),
          );
        }

        return operations;
      }),
    );

    const { fulfilled = [], rejected = [] } = groupBy(results, "status");

    if (rejected.length) {
      context.environment.logger.error(
        { errors: (rejected as PromiseRejectedResult[]).map((r) => r.reason) },
        "[triageThreads] Some threads failed to be triaged",
      );
    }

    const operations = (fulfilled as PromiseFulfilledResult<Operation[]>[]).flatMap((r) => r.value);

    return {
      name: "notification.triageThreads",
      props: {
        ...props,
        threads: threads.map((threadData) => ({
          ...threadData,
          newMatchingTagIds: newMatchingTagIdsByThreadId[threadData.threadId],
        })),
      },
      operations: operations.flat(),
    };
  },
  authorize: async (props, context) => {
    const notificationIds = props.threads.map((thread) =>
      generateRecordId("notification", {
        thread_id: thread.threadId,
        user_id: context.currentUserId,
      }),
    );

    await authorizeUserCanUpdateNotifications({ notificationIds: notificationIds, context });
  },
};

/* -----------------------------------------------------------------------------------------------*/

// Note that this function implementation is specific to the `triageThreads` action in that it
// doesn't handle transaction conflicts well.
async function getCreateNotificationOperationsAndMatchingTagIds(
  context: ActionContext,
  props: {
    ownerOrganizationId: string;
    threadId: string;
    done: boolean | undefined;
    remindAt: string | null | undefined;
    isStarred: boolean | undefined;
    areRequestsResolved?: true;
    /**
     * If this is passed in, we will use it on the server to preemptively "index" the notification under those tags
     * until the next reindex run.
     */
    newMatchingTagIds?: string[];
    /**
     * If the user has lost permission to access this thread, provide `null`. Else
     * provide the appropriate values.
     */
    lastMessage: {
      id: string;
      timelineOrder: string;
      sentAt: string;
    };
    currentUsersRequests: RecordValue<"thread_request">[];
  },
): Promise<{ operations: Operation[]; matchingTagIds?: string[] }> {
  const { ownerOrganizationId } = props;

  const isDone = props.done ?? true;
  const now = new Date().toISOString();
  let requestPriority: number | null = null;
  let respondBy: string | null = null;
  let respondByTz: string | null = null;

  if (props.areRequestsResolved || props.currentUsersRequests.length === 0) {
    requestPriority = null;
    respondBy = null;
    respondByTz = null;
  } else {
    for (const request of props.currentUsersRequests) {
      if (request.resolved_at) continue;

      if (requestPriority === null || request.priority < requestPriority) {
        requestPriority = request.priority;
      }

      if (respondBy === null || dayjs(request.respond_by).isBefore(respondBy)) {
        respondBy = request.respond_by;
        respondByTz = request.respond_by_tz;
      }
    }
  }

  const notificationId = generateRecordId("notification", {
    thread_id: props.threadId,
    user_id: context.currentUserId,
  });

  const notification: CreateRecord<"notification"> = {
    id: notificationId,
    user_id: context.currentUserId,
    thread_id: props.threadId,
    thread_type: "COMMS",
    message_id: props.lastMessage.id,
    tag_ids: [],
    done_at: isDone ? now : null,
    is_done: isDone,
    done_last_modified_by: "user",
    oldest_message_not_marked_done_message_id: isDone ? null : props.lastMessage.id,
    oldest_message_not_marked_done_sent_at: isDone ? null : props.lastMessage.sentAt,
    has_reminder: !!props.remindAt,
    is_starred: !!props.isStarred,
    owner_organization_id: ownerOrganizationId,
    priority: 200,
    request_priority: requestPriority,
    respond_by: respondBy,
    respond_by_tz: respondByTz,
    remind_at: props.remindAt || null,
    sent_at: props.lastMessage.sentAt,
    starred_at: props.isStarred ? now : null,
    // Set is_delivered to false so that the server reindexes this notification.
    is_delivered: false,
    delivered_at: null,
    data: null,
  };

  const operations: Operation[] = [op.set("notification", notification)];

  let newMatchingTagIds: string[] | undefined;

  // When running on the client, we want to optimistically deliver this notification to the user's inbox.
  // On the server this message will be indexed by our standard deliver notification
  // background job logic (so this step doesn't need to happen on the server).
  if (context.type === "client") {
    const loader = createMemoizedDatabase(context.environment.loader, {
      recordMap: {
        notification: {
          // Since this notification hasn't been persisted yet, we need to update the memoized database's cache
          // with this notification so that any inbox rules which need this notification will be able to get it.
          [notification.id]: notification as RecordValue<"notification">,
        },
      },
    });

    newMatchingTagIds = await getReindexNotificationInboxTagsMatches(
      {
        ...context,
        environment: { ...context.environment, loader },
      },
      { threadId: notification.thread_id },
    );
  } else {
    newMatchingTagIds = props.newMatchingTagIds;
  }

  if (newMatchingTagIds) {
    const notificationPointer = getPointer("notification", notificationId);

    if (context.type === "server") {
      // If the client was offline for a while and is now uploading changes to the server,
      // it's possible that these tags were deleted or no longer exist. In this case we
      // want to ignore them and just perform reindexing on the server.
      const [matchingTags] = await context.environment.loader.getRecords(
        newMatchingTagIds.map((id) => getPointer("tag", id)),
      );

      if (matchingTags.length === newMatchingTagIds.length) {
        operations.push(op.set(notificationPointer, ["tag_ids"], newMatchingTagIds));
      } else {
        context.environment.logger.warn(
          { newMatchingTagIds, matchingTags, context: { currentUserId: context.currentUserId, type: context.type } },
          "[notification.getCreateNotificationOperations] Some matching tags were not found",
        );
      }
    } else {
      operations.push(op.set(notificationPointer, ["tag_ids"], newMatchingTagIds));
    }
  } else {
    context.environment.logger.error(
      {
        props,
        context: { currentUserId: context.currentUserId, type: context.type },
      },
      "[notification.getCreateNotificationOperations] newMatchingTagIds was not set",
    );
  }

  if (
    notification.is_done &&
    // setting a reminder shouldn't mark a thread as "read"
    !notification.has_reminder
  ) {
    operations.push(
      op.set("thread_read_receipt", {
        id: generateRecordId("thread_read_receipt", {
          thread_id: notification.thread_id,
          user_id: context.currentUserId,
        }),
        owner_organization_id: ownerOrganizationId,
        thread_id: notification.thread_id,
        user_id: context.currentUserId,
        read_to_timeline_id: generateRecordId("thread_timeline", {
          entry_id: props.lastMessage.id,
          thread_id: notification.thread_id,
        }),
        read_to_timeline_order: props.lastMessage.timelineOrder,
      }),

      op.set("thread_seen_receipt", {
        id: generateRecordId("thread_seen_receipt", {
          thread_id: notification.thread_id,
          user_id: context.currentUserId,
        }),
        owner_organization_id: ownerOrganizationId,
        thread_id: notification.thread_id,
        user_id: context.currentUserId,
        seen_to_timeline_id: generateRecordId("thread_timeline", {
          entry_id: props.lastMessage.id,
          thread_id: notification.thread_id,
        }),
        seen_to_timeline_order: props.lastMessage.timelineOrder,
      }),
    );
  }

  return { operations, matchingTagIds: newMatchingTagIds };
}

/* -------------------------------------------------------------------------------------------------
 *  undoTriageThreads
 * -------------------------------------------------------------------------------------------------
 */

type UndoTriageThreadsProps = {
  threads: UndoTriageThreadData[];
};

export type UndoTriageThreadData = {
  threadId: string;
  /**
   * We expect the notification to have a specific message_id value. If the notification doesn't,
   * then that indicates that a new message has been delivered to this thread since the user
   * constructed this action. If this happens, our triage action is liable to overwrite any updates
   * to this notification caused by the delivery of the new message, which would be undesireable.
   * In this case we'll choose to throw an error instead of run this action.
   */
  expectedNotificationMessageId: string;

  notification: Pick<
    RecordValue<"notification">,
    | "is_done"
    | "done_at"
    | "done_last_modified_by"
    | "oldest_message_not_marked_done_message_id"
    | "oldest_message_not_marked_done_sent_at"
    | "has_reminder"
    | "remind_at"
    | "is_starred"
    | "starred_at"
    | "request_priority"
    | "respond_by"
    | "respond_by_tz"
  > | null;

  currentUserThreadRequests?: RecordValue<"thread_request">[];

  receiptValues?: {
    upToTimelineId: string;
    upToTimelineOrder: string;
  } | null;
};

export const undoTriageThreads = createAction<UndoTriageThreadsProps>("notification.undoTriageThreads", {
  operations: async (props, context) => {
    const { threads } = props;

    if (!threads.length) {
      throw new InvalidActionStateError("[undoTriageThreads] Need at least one thread to triage", {
        errorData: { props },
      });
    }

    const [userProfile] = await context.environment.loader.getRecord("user_profile", context.currentUserId);

    if (!userProfile) {
      throw new InvalidActionStateError("[undoTriageThreads] User profile not found", { errorData: { props } });
    }

    const ownerOrganizationId = userProfile.owner_organization_id;

    const results = await Promise.allSettled(
      threads.map(async (threadData): Promise<Operation[]> => {
        const operations: Operation[] = [];

        const notificationPointer = getPointer("notification", {
          thread_id: threadData.threadId,
          user_id: context.currentUserId,
        });

        const [existingNotification] = await context.environment.loader.getRecord(notificationPointer);

        if (!existingNotification) {
          // If we're undoing a notification triage, this notification should have already been
          // created.
          throw new InvalidActionStateError("[undoTriageThreads] Notification not found", { errorData: { props } });
        }

        if (existingNotification.message_id !== threadData.expectedNotificationMessageId) {
          // We don't want to undo a user's triage if the thread associated with this notification
          // has received a new message since our "undo" might inadvertently overwrite new delivery
          // information.
          throw new InvalidActionStateError("[undoTriageThreads] expected message ID has changed", {
            errorData: { props },
          });
        }

        if (threadData.notification) {
          operations.push(
            op.update(
              notificationPointer,
              pick(threadData.notification, [
                "is_done",
                "done_at",
                "done_last_modified_by",
                "oldest_message_not_marked_done_message_id",
                "oldest_message_not_marked_done_sent_at",
                "has_reminder",
                "remind_at",
                "is_starred",
                "starred_at",
                "request_priority",
                "respond_by",
                "respond_by_tz",
              ]),
            ),
          );
        } else {
          operations.push(op.delete(notificationPointer));
        }

        if (isDefined(threadData.receiptValues)) {
          if (threadData.receiptValues) {
            operations.push(
              op.set("thread_read_receipt", {
                id: generateRecordId("thread_read_receipt", {
                  thread_id: threadData.threadId,
                  user_id: context.currentUserId,
                }),
                owner_organization_id: ownerOrganizationId,
                thread_id: threadData.threadId,
                user_id: context.currentUserId,
                read_to_timeline_id: threadData.receiptValues.upToTimelineId,
                read_to_timeline_order: threadData.receiptValues.upToTimelineOrder,
              }),

              op.set("thread_seen_receipt", {
                id: generateRecordId("thread_seen_receipt", {
                  thread_id: threadData.threadId,
                  user_id: context.currentUserId,
                }),
                owner_organization_id: ownerOrganizationId,
                thread_id: threadData.threadId,
                user_id: context.currentUserId,
                seen_to_timeline_id: threadData.receiptValues.upToTimelineId,
                seen_to_timeline_order: threadData.receiptValues.upToTimelineOrder,
              }),
            );
          } else {
            operations.push(
              op.delete(
                getPointer("thread_read_receipt", {
                  thread_id: threadData.threadId,
                  user_id: context.currentUserId,
                }),
              ),
              op.delete(
                getPointer("thread_seen_receipt", {
                  thread_id: threadData.threadId,
                  user_id: context.currentUserId,
                }),
              ),
            );
          }
        }

        if (isDefined(threadData.currentUserThreadRequests)) {
          for (const request of threadData.currentUserThreadRequests) {
            operations.push(
              ...ops.threadRequest.updateThreadRequestLastResolvedAt({
                request,
                lastResolvedAt:
                  request.last_resolved_at && request.last_resolved_at_timeline_order ?
                    {
                      sent_at: request.last_resolved_at,
                      timeline_order: request.last_resolved_at_timeline_order,
                    }
                  : null,
              }),
            );
          }
        }

        return operations;
      }),
    );

    const { fulfilled = [], rejected = [] } = groupBy(results, "status");

    if (rejected.length) {
      context.environment.logger.error(
        { errors: (rejected as PromiseRejectedResult[]).map((r) => r.reason) },
        "[notification.undoTriageThreads] Some threads failed to undo",
      );
    }

    const operations = (fulfilled as PromiseFulfilledResult<Operation[]>[]).flatMap((r) => r.value);

    return operations;
  },
  authorize: async (props, context) => {
    context.environment.logger.warn("notification.undoTriageThreads.authorize not yet implemented!");
  },
});

/* -------------------------------------------------------------------------------------------------
 *  deleteNotification
 * -------------------------------------------------------------------------------------------------
 */

type DeleteNotificationProps = {
  threadId: string;
};

export const deleteNotification = createAction<DeleteNotificationProps>("notification.deleteNotification", {
  operations: async (props, context) => {
    return op.delete(getPointer("notification", { user_id: context.currentUserId, thread_id: props.threadId }));
  },
  authorize: async (props, context) => {
    const notificationId = generateRecordId("notification", {
      thread_id: props.threadId,
      user_id: context.currentUserId,
    });

    await authorizeUserCanUpdateNotifications({
      notificationIds: [notificationId],
      context,
    });
  },
});

/* -------------------------------------------------------------------------------------------------
 *  reindexNotification
 * -------------------------------------------------------------------------------------------------
 */

type ReindexNotificationProps = {
  threadId: string;
  newMatchingTagIds?: string[];
};

export const reindexNotification: ActionCreator<ReindexNotificationProps> = {
  name: "notification.reindexNotification",
  action: async (props, context) => {
    const notificationPointer = getPointer("notification", {
      user_id: context.currentUserId,
      thread_id: props.threadId,
    });

    const operations: Operation[] = [
      // Set is_delivered to false so that the server reindexes this notification.
      op.update(notificationPointer, {
        is_delivered: false,
        delivered_at: null,
      }),
    ];

    const newMatchingTagIds =
      context.type === "client" ?
        // When running on the client, we want to optimistically reindex this notification.
        await getReindexNotificationInboxTagsMatches(context, props)
        // When running on the server, we'll accept the props from the client and use those values until the
        // next reindex run
      : props.newMatchingTagIds;

    if (newMatchingTagIds) {
      if (context.type === "server") {
        // If the client was offline for a while and is now uploading changes to the server,
        // it's possible that these tags were deleted or no longer exist. In this case we
        // want to ignore them and just perform reindexing on the server.
        const [matchingTags] = await context.environment.loader.getRecords(
          newMatchingTagIds.map((id) => getPointer("tag", id)),
        );

        if (matchingTags.length === newMatchingTagIds.length) {
          operations.push(op.set(notificationPointer, ["tag_ids"], newMatchingTagIds));
        } else {
          context.environment.logger.warn(
            { newMatchingTagIds, matchingTags, context: { currentUserId: context.currentUserId, type: context.type } },
            "[notification.reindexNotification] Some matching tags were not found",
          );
        }
      } else {
        operations.push(op.set(notificationPointer, ["tag_ids"], newMatchingTagIds));
      }
    } else {
      context.environment.logger.error(
        {
          props,
          context: { currentUserId: context.currentUserId, type: context.type },
        },
        "[notification.reindexNotification] newMatchingTagIds was not set",
      );
    }

    return {
      name: "notification.reindexNotification",
      props: {
        ...props,
        newMatchingTagIds,
      },
      operations,
    };
  },
  authorize: async (props, context) => {
    const notificationId = generateRecordId("notification", {
      thread_id: props.threadId,
      user_id: context.currentUserId,
    });

    await Promise.all([
      authorizeUserHasAccessToTags({ tagIds: props.newMatchingTagIds ?? [], context }),
      authorizeUserCanUpdateNotifications({
        notificationIds: [notificationId],
        context,
      }),
    ]);
  },
};

/* -------------------------------------------------------------------------------------------------
 *  getReindexNotificationOperations
 * -------------------------------------------------------------------------------------------------
 */

async function getReindexNotificationInboxTagsMatches(
  context: ActionContext,
  props: {
    threadId: string;
  },
): Promise<string[]> {
  const { threadId } = props;
  const {
    currentUserId,
    environment: { loader, logger },
  } = context;

  const notificationPointer = getPointer("notification", { user_id: currentUserId, thread_id: threadId });

  const [[notification], [_inboxSections], [_inboxSubsections], [messages]] = await Promise.all([
    loader.getRecord(notificationPointer),
    loader.getInboxSections({ currentUserId }),
    loader.getInboxSubsections({ currentUserId }),
    loader.getMessages({ thread_id: threadId }),
  ]);

  if (!notification) {
    throw new InvalidActionStateError(
      `[getReindexNotificationOperations] Notification not found: ${notificationPointer.id}`,
    );
  }

  const inboxSections = _inboxSections as InboxSectionTagRecord[];
  const inboxSubsections = _inboxSubsections as InboxSubsectionTagRecord[];

  const inboxSectionMatches = compact(
    await Promise.all(
      inboxSections.map((inboxSection) => {
        return findMatchingSubsection({
          currentUserId,
          threadId,
          messages,
          loader,
          inboxSection,
          inboxSubsections: inboxSubsections.filter(
            (subsection) => subsection.data.inbox_section_id === inboxSection.id,
          ),
          logger,
        });
      }),
    ),
  );

  if (inboxSectionMatches.length === 0) {
    logger.error(
      { notification, inboxSections, inboxSubsections },
      `[applyReindexNotificationToTx] No inbox section matches found for notification ${notification.id}`,
    );

    return [];
  }

  const tagIds = new Set<string>();

  for (const { section, subsection } of inboxSectionMatches) {
    tagIds.add(section.id);
    tagIds.add(subsection.id);
  }

  return Array.from(tagIds);
}

export const actions = {
  triageThreads,
  undoTriageThreads,
  deleteNotification,
  reindexNotification,
};
